import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ShieldCheck, Download, Award, Target, Eye, Users } from 'lucide-react';
import PageHero from '../components/PageHero';

export default function AboutUs() {
  const { lang, t } = useLanguage();

  return (
    <div className="about-us-page">
      {/* Page Hero */}
      <PageHero eyebrow="Profil Korporat" title={t('nav_about')} subtitle="Mitra terpercaya penyedia produk beton dan agregat berkualitas tinggi di Indonesia (Member of SIG)." image="/reference-images/about-company.webp" />

      {/* Breadcrumbs */}
      <div className="breadcrumb-wrapper">
        <div className="container">
          <ul className="breadcrumb-list">
            <li><Link to="/">{t('nav_home')}</Link></li>
            <li><span className="separator">/</span></li>
            <li><span className="current">{t('nav_about')}</span></li>
          </ul>
        </div>
      </div>

      {/* Profile Overview Section */}
      <section className="section">
        <div className="container">
          <div className="grid-2 align-center">
            <div>
              <span className="accent-bar"></span>
              <span className="eyebrow">Tentang Solusi Bangun Beton</span>
              <h2 className="section-title">Pelopor Solusi Beton Terintegrasi di Indonesia</h2>
              <p className="about-desc-text">
                PT Solusi Bangun Beton (SBB) adalah anak perusahaan dari PT Solusi Bangun Indonesia Tbk dan bagian dari PT Semen Indonesia (Persero) Tbk (SIG), penyedia solusi bahan bangunan terbesar di Asia Tenggara.
              </p>
              <p className="about-desc-text">
                SBB fokus memproduksi dan mengoperasikan beton siap pakai (ready-mix), beton solusi teraplikasi presisi tinggi, dan tambang agregat batuan keras. Dengan lebih dari 30 batching plant yang tersebar di wilayah strategis Indonesia, kami mendukung proyek infrastruktur, gedung tinggi, industri, hingga pemukiman.
              </p>

              <div className="corporate-badges">
                <div className="corp-badge">
                  <ShieldCheck size={20} color="var(--color-brand-primary)" />
                  <span>Jaminan Standar Mutu SIG</span>
                </div>
                <div className="corp-badge">
                  <Award size={20} color="var(--color-brand-primary)" />
                  <span>Sertifikasi ISO 9001 & PROPER</span>
                </div>
              </div>
            </div>

            <div>
              <img 
                src="/reference-images/about-company.webp" 
                alt="Operasional Batching Plant SBB" 
                className="img-rounded-shadow"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission (Section 5.5) */}
      <section className="section section-subtle">
        <div className="container">
          <div className="section-header text-center">
            <span className="accent-bar"></span>
            <h2 className="section-title">Visi & Misi Perusahaan</h2>
          </div>

          <div className="grid-2">
            <div className="vm-card">
              <div className="vm-icon-circle">
                <Eye size={28} color="var(--color-brand-primary)" />
              </div>
              <h3 className="vm-title">Visi Kami</h3>
              <p className="vm-desc">
                Menjadi penyedia solusi beton dan bahan bangunan terdepan di Indonesia yang terpercaya, inovatif, serta ramah lingkungan.
              </p>
            </div>

            <div className="vm-card">
              <div className="vm-icon-circle">
                <Target size={28} color="var(--color-brand-primary)" />
              </div>
              <h3 className="vm-title">Misi Kami</h3>
              <ul className="vm-list">
                <li>Menyediakan produk beton presisi dan agregat berkualitas tinggi sesuai standar SNI & Internasional.</li>
                <li>Menerapkan teknologi ramah iklim dan efisiensi material untuk masa depan berkelanjutan.</li>
                <li>Memberikan pelayanan operasional dan pengiriman tepat waktu dengan keselamatan kerja (K3) tanpa kompromi.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Organizational Structure Diagram (Section 5.5) */}
      <section className="section">
        <div className="container">
          <div className="section-header text-center">
            <span className="accent-bar"></span>
            <span className="eyebrow">Tata Kelola Perusahaan</span>
            <h2 className="section-title">Struktur Organisasi</h2>
          </div>

          <div className="org-structure-box">
            <div className="org-node leader">
              <strong>Dewan Direksi & Manajemen Utama</strong>
              <span>PT Solusi Bangun Beton</span>
            </div>
            
            <div className="org-connector-line"></div>

            <div className="org-nodes-grid">
              <div className="org-node">
                <strong>Direktorat Operasional & Plant</strong>
                <span>Pengelolaan 30+ Batching Plants</span>
              </div>
              <div className="org-node">
                <strong>Direktorat Teknikal & Quality Assurance</strong>
                <span>Laboratorium & Inovasi Beton</span>
              </div>
              <div className="org-node">
                <strong>Direktorat Komersial & Pemasaran</strong>
                <span>Layanan Pelanggan & Penjualan</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Logo Philosophy & Asset Download (Section 5.5) */}
      <section className="section section-subtle">
        <div className="container">
          <div className="grid-2 align-center">
            <div>
              <span className="accent-bar"></span>
              <span className="eyebrow">Identitas Merek</span>
              <h2 className="section-title">Filosofi Logo & Aset Resmi</h2>
              <p className="about-desc-text">
                Logo Solusi Bangun Beton mencerminkan ketegasan struktural, energi brand merah SIG, dan komitmen keberlanjutan. Dominasi bidang geometris merah melambangkan fondasi kokoh untuk pembangunan Indonesia.
              </p>
              
              <button className="btn btn-primary" onClick={() => alert('Mengunduh Paket Aset Logo SBB (SVG/PNG)...')}>
                <Download size={18} />
                <span>Unduh Aset Logo Resmi (.ZIP)</span>
              </button>
            </div>

            <div className="logo-display-card text-center">
              <div className="logo-preview-box">
                <span className="logo-main" style={{ fontSize: '28px' }}>SOLUSI BANGUN BETON</span>
                <span className="logo-sub" style={{ fontSize: '14px', marginTop: '4px' }}>Member of SIG</span>
              </div>
              <p className="logo-caption">Aset Resmi Solusi Bangun Beton Tbk</p>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .corporate-badges {
          display: flex;
          gap: 16px;
          margin-top: 24px;
        }

        .corp-badge {
          display: flex;
          align-items: center;
          gap: 8px;
          background-color: var(--color-surface-subtle);
          padding: 10px 16px;
          border-radius: var(--border-radius-sm);
          font-size: 13px;
          font-weight: 600;
          color: var(--color-ink);
        }

        .vm-card {
          background-color: #ffffff;
          padding: 36px;
          border-radius: var(--border-radius-md);
          border: 1px solid var(--color-border);
          box-shadow: var(--shadow-subtle);
        }

        .vm-icon-circle {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background-color: var(--color-brand-primary-light);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 20px;
        }

        .vm-title {
          font-size: 22px;
          font-weight: 800;
          color: var(--color-ink);
          margin-bottom: 12px;
        }

        .vm-desc {
          font-size: 15px;
          color: var(--color-text-muted);
          line-height: 1.6;
        }

        .vm-list {
          list-style: disc;
          padding-left: 20px;
          display: flex;
          flex-direction: column;
          gap: 8px;
          font-size: 14px;
          color: var(--color-text-muted);
        }

        .org-structure-box {
          display: flex;
          flex-direction: column;
          align-items: center;
          margin-top: 30px;
        }

        .org-node {
          background-color: var(--color-surface-subtle);
          border: 2px solid var(--color-border);
          border-radius: var(--border-radius-md);
          padding: 16px 24px;
          text-align: center;
          display: flex;
          flex-direction: column;
        }

        .org-node.leader {
          border-color: var(--color-brand-primary);
          background-color: #ffffff;
        }

        .org-node strong {
          font-size: 16px;
          color: var(--color-ink);
        }

        .org-node span {
          font-size: 12px;
          color: var(--color-text-muted);
        }

        .org-connector-line {
          width: 2px;
          height: 40px;
          background-color: var(--color-border);
          margin: 12px 0;
        }

        .org-nodes-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
          width: 100%;
        }

        .logo-display-card {
          background-color: #ffffff;
          border: 1px solid var(--color-border);
          border-radius: var(--border-radius-md);
          padding: 40px;
          box-shadow: var(--shadow-hover);
        }

        .logo-preview-box {
          padding: 30px;
          background-color: var(--color-surface-subtle);
          border-radius: 8px;
          margin-bottom: 12px;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .logo-caption {
          font-size: 12px;
          color: var(--color-text-muted);
        }

        @media (max-width: 768px) {
          .org-nodes-grid {
            grid-template-columns: 1fr;
          }
          .corporate-badges {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
}
